import React, { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import muiTheme from '@/theme/muiTheme';
import { AppShell } from '@/features/layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useExportJobs } from '@/hooks/useExportJobs';

/** Factory — creates a fresh QueryClient (new per mount, isolated in tests). */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes default
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * AppRuntimeHooks
 *
 * Mount application-level hooks exactly once.
 * useExportJobs listens to selected sample changes and triggers export generation.
 */
const AppRuntimeHooks: React.FC = () => {
  useExportJobs();
  return null;
};

const App: React.FC = () => {
  const queryClient = useMemo(() => makeQueryClient(), []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <AppRuntimeHooks />
          <AppShell />
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;